import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Calendar, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { useBrandName, useBrandShort, useSettings } from '../context/SettingsContext';
import { BTN_PRIMARY, BTN_OUTLINE, DEFAULT_BRAND, RADIUS_SURFACE, mediaSrc } from '../config/brand';
import EditableText from './EditableText';

const OFFER_PATHS = ['/ateliers', '/cursus', '/stages'];

const navLinkClass = (active) =>
  `text-sm font-medium transition-colors rounded-md px-1 py-0.5 ${
    active ? 'text-[#3a00e0]' : 'text-slate-600 hover:text-[#c47a0a]'
  }`;

const Navbar = () => {
  const brandName = useBrandName();
  const brandShort = useBrandShort();
  const { settings } = useSettings();
  const logoIconUrl = settings.logo_url || DEFAULT_BRAND.logo_url;
  const logoLongUrl =
    settings.logo_long_url || DEFAULT_BRAND.logo_long_url || logoIconUrl;
  const [isOpen, setIsOpen] = useState(false);
  const [showOffersDropdown, setShowOffersDropdown] = useState(false);
  const [logoFlash, setLogoFlash] = useState(false);
  const logoClickCount = useRef(0);
  const logoClickTimer = useRef(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isOffersActive = OFFER_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    setIsOpen(false);
    setShowOffersDropdown(false);
  }, [location.pathname]);

  const handleLogoClick = () => {
    logoClickCount.current += 1;
    clearTimeout(logoClickTimer.current);
    logoClickTimer.current = setTimeout(() => {
      logoClickCount.current = 0;
    }, 700);

    if (logoClickCount.current >= 3) {
      logoClickCount.current = 0;
      setLogoFlash(true);
      toast('Bip-bip !', {
        description: `Robot ${brandName} en ligne. Prêt à coder ?`,
        duration: 3000,
      });
      setTimeout(() => setLogoFlash(false), 400);
    }
  };

  const offerLinks = [
    { to: '/ateliers', label: 'Ateliers Découverte', editKey: 'navbar.offers.ateliers' },
    { to: '/cursus', label: 'Cursus Annuel', editKey: 'navbar.offers.cursus' },
    { to: '/stages', label: 'Stages Vacances', editKey: 'navbar.offers.stages' },
  ];

  const mainLinks = [
    { to: '/', label: 'Accueil', editKey: 'navbar.home' },
    { to: '/boutique', label: 'Boutique', editKey: 'navbar.boutique' },
    { to: '/elearning', label: 'E-learning', editKey: 'navbar.elearning' },
    { to: '/entreprises', label: 'Entreprises', editKey: 'navbar.entreprises' },
    { to: '/blog', label: 'Blog', editKey: 'navbar.blog' },
    { to: '/contact', label: 'Contact', editKey: 'navbar.contact' },
  ];

  return (
    <>
      <nav className="site-navbar fixed z-[100]">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link
            to="/"
            onClick={handleLogoClick}
            className={`flex items-center min-w-0 shrink beep-hover ${logoFlash ? 'logo-beep-flash' : ''}`}
            aria-label={`${brandName} — Accueil`}
          >
            {logoLongUrl || logoIconUrl ? (
              <span className="inline-flex items-center max-w-[min(100%,7rem)] min-[380px]:max-w-[min(100%,8.75rem)] sm:max-w-[13rem] lg:max-w-[220px]">
                <img
                  src={mediaSrc(logoLongUrl || logoIconUrl)}
                  alt={brandName}
                  className="h-9 sm:h-10 lg:h-12 w-auto max-w-full object-contain object-left"
                />
              </span>
            ) : (
              <>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#3a00e0] to-[#18449e] ${RADIUS_SURFACE} flex items-center justify-center shadow-lg shadow-[0_10px_24px_-10px_rgba(58,0,224,0.45)]`}>
                  <span className="text-white font-bold text-lg">{brandShort}</span>
                </div>
                <span className="text-slate-900 font-bold text-base sm:text-lg tracking-wide ml-2.5">
                  {brandName}
                </span>
              </>
            )}
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            <Link to="/" className={navLinkClass(isActive('/'))}>
              <EditableText editKey="navbar.home" defaultValue="Accueil" as="span" />
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setShowOffersDropdown(true)}
              onMouseLeave={() => setShowOffersDropdown(false)}
            >
              <button
                type="button"
                className={`flex items-center gap-1 text-sm font-medium transition-colors py-2 ${
                  isOffersActive ? 'text-[#3a00e0]' : 'text-slate-600 hover:text-[#c47a0a]'
                }`}
                aria-expanded={showOffersDropdown}
              >
                <EditableText editKey="navbar.offers.label" defaultValue="Nos Offres" as="span" />
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${showOffersDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {showOffersDropdown && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-56 z-[110]">
                  <div className={`bg-white ${RADIUS_SURFACE} shadow-xl border border-slate-200 py-1.5 overflow-hidden`}>
                    {offerLinks.map(({ to, label, editKey }) => (
                      <Link
                        key={to}
                        to={to}
                        className={`block px-4 py-2.5 text-sm transition-colors ${
                          isActive(to)
                            ? 'bg-[rgba(58,0,224,0.06)] text-[#3a00e0] font-medium'
                            : 'text-slate-600 hover:bg-[rgba(250,176,58,0.08)] hover:text-[#c47a0a]'
                        }`}
                      >
                        <EditableText editKey={editKey} defaultValue={label} as="span" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {mainLinks.slice(1).map(({ to, label, editKey }) => (
              <Link key={to} to={to} className={navLinkClass(isActive(to))}>
                <EditableText editKey={editKey} defaultValue={label} as="span" />
              </Link>
            ))}

            <div className="flex items-center gap-3 pl-1">
              <Link to="/reserver">
                <Button className={`${BTN_PRIMARY} btn-beep h-9 px-5`}>
                  <Calendar className="h-4 w-4" />
                  <EditableText editKey="navbar.cta_reserve" defaultValue="Réserver un atelier" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Mobile : CTA Réserver toujours visible + burger */}
          <div className="lg:hidden flex items-center gap-1 sm:gap-2 shrink-0">
            <Link to="/reserver" className="shrink-0">
              <Button className={`${BTN_PRIMARY} btn-beep h-8 sm:h-9 px-2 sm:px-3.5 text-[11px] leading-none min-[380px]:text-xs sm:text-sm`}>
                <Calendar className="hidden min-[420px]:block h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <EditableText editKey="navbar.cta_reserve" defaultValue="Réserver un atelier" />
              </Button>
            </Link>
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu — liens scrollables, CTAs pinés en bas */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[min(36rem,calc(100dvh-8rem))] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="flex flex-col max-h-[min(36rem,calc(100dvh-8rem))] bg-white/95 backdrop-blur-xl border-t border-slate-200">
          <div className="overflow-y-auto overscroll-contain px-4 py-3 space-y-1 min-h-0">
            <Link
              to="/"
              className={`block px-3 py-2.5 rounded-lg transition-colors ${
                isActive('/')
                  ? 'bg-[rgba(58,0,224,0.06)] text-[#3a00e0] font-medium'
                  : 'text-slate-600 hover:bg-[rgba(250,176,58,0.08)] hover:text-[#c47a0a]'
              }`}
            >
              <EditableText editKey="navbar.home" defaultValue="Accueil" as="span" />
            </Link>

            <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <EditableText editKey="navbar.offers.label" defaultValue="Nos Offres" as="span" />
            </p>
            {offerLinks.map(({ to, label, editKey }) => (
              <Link
                key={to}
                to={to}
                className={`block px-3 py-2.5 rounded-lg transition-colors ${
                  isActive(to)
                    ? 'bg-[rgba(58,0,224,0.06)] text-[#3a00e0] font-medium'
                    : 'text-slate-600 hover:bg-[rgba(250,176,58,0.08)] hover:text-[#c47a0a]'
                }`}
              >
                <EditableText editKey={editKey} defaultValue={label} as="span" />
              </Link>
            ))}

            <div className="border-t border-slate-100 my-2" />

            {mainLinks.slice(1).map(({ to, label, editKey }) => (
              <Link
                key={to}
                to={to}
                className={`block px-3 py-2.5 rounded-lg transition-colors ${
                  isActive(to)
                    ? 'bg-[rgba(58,0,224,0.06)] text-[#3a00e0] font-medium'
                    : 'text-slate-600 hover:bg-[rgba(250,176,58,0.08)] hover:text-[#c47a0a]'
                }`}
              >
                <EditableText editKey={editKey} defaultValue={label} as="span" />
              </Link>
            ))}
          </div>

          <div className="shrink-0 px-4 pt-2 pb-3 space-y-2 border-t border-slate-100 bg-white/95">
            <Link to="/reserver" className="block">
              <Button className={`w-full ${BTN_PRIMARY} btn-beep`}>
                <Calendar className="h-4 w-4" />
                <EditableText editKey="navbar.cta_reserve" defaultValue="Réserver un atelier" />
              </Button>
            </Link>
            <Link to="/contact" className="block">
              <Button variant="outline" className={`w-full ${BTN_OUTLINE}`}>
                <Mail className="h-4 w-4" />
                <EditableText editKey="navbar.contact_cta" defaultValue="Nous contacter" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </nav>
    {/* Cible du skip-link — après la nav, avant le contenu de page */}
    <div id="contenu" tabIndex={-1} className="outline-none" />
    </>
  );
};

export default Navbar;
